import './styles/fonts.css';
import './styles/main.css';
import { App } from './app/app';
import { attachEditorInput, attachKeyboard, attachStageInput } from './app/input';
import { UI } from './app/ui';

const get = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} not found`);
  return el as T;
};

// ?og renders the social-preview card (used by scripts/screenshots.mjs for public/og-image.png)
if (new URLSearchParams(location.search).has('og')) document.documentElement.classList.add('og');

async function main(): Promise<void> {
  const ui = new UI();
  const app = new App(
    {
      stage: get('stage'),
      plane: get<HTMLCanvasElement>('plane'),
      overlay: get<HTMLCanvasElement>('overlay'),
      editor: get<HTMLCanvasElement>('editor'),
    },
    ui.callbacks(),
  );
  ui.app = app;
  const forceCpu = new URLSearchParams(location.search).get('renderer') === 'cpu';
  await app.start(forceCpu);
  ui.bind(app);
  attachStageInput(app, get<HTMLCanvasElement>('overlay'));
  attachEditorInput(app, app.editorCanvas, get('editor-cursor'));
  attachKeyboard(app, get('overlay'), { escape: () => ui.escape(), showKeys: () => ui.showKeys() });

  // A small, read-mostly handle for end-to-end tests and the curious.
  Object.assign(window, {
    __horocycle: {
      get renderer() {
        return app.rendererKind;
      },
      state: () => app.store.get(),
      set: (patch: Parameters<typeof app.store.set>[0]) => app.store.set(patch),
      samplePixels: (pts: { x: number; y: number }[]) => app.samplePixels(pts),
      view: () => app.viewSnapshot(),
      strokes: () => app.painter.motif.strokes.length,
      diskRadius: () => app.diskRadiusPx,
      stageSize: () => ({ width: app.camera.width, height: app.camera.height }),
      displayModel: () => ({ model: app.displayModel, morph: app.morph }),
      measurement: () => app.measurement?.readout() ?? null,
    },
  });
}

main().catch((err: unknown) => {
  console.error(err);
  const notice = document.getElementById('notice');
  if (notice) {
    notice.hidden = false;
    notice.dataset.kind = 'error';
    notice.innerHTML =
      '<p><strong>Horocycle could not start.</strong> Please reload the page; if it keeps happening, your browser may be missing a feature it needs.</p>';
    const b = document.createElement('button');
    b.className = 'text-button';
    b.type = 'button';
    b.textContent = 'Reload';
    b.addEventListener('click', () => location.reload());
    notice.append(b);
  }
});
